// DS2 — 2단 hero(시안 base .hero · hub_extra 변형). 홈·지역 허브 공용 — 데이터는 props 로만(숫자는 DB 실값).
// ds3 밀도: 1.15fr:1fr gap 24. 좌: eyebrow 13/700 navy2 → h1 28(≤760 22 · hub 26/22) → 부제 13 → 검색(kd-gs lg — search slot) → 칩(위 8) 6 → 숫자 4(위 14 · 19/700, ≤760 17 · 라벨 12 nowrap).
// 우: 대표 현장 카드(.feat 라운드12 · min-h 236/180 · 하단 그라데이션 · 흰 태그 · 이름 20/700 · 메타 13 · 우상단 「AI 상상도」 — 공식 이미지면 aiLabel 없음). ≤760 1단 = 대표 카드는 숫자 아래(DOM 순서).
// ⚠️ h1 문자열은 호출부 그대로(홈 rank_lock·title·h1 불변식은 호출부 책임). 이 부품은 문구를 만들지 않는다.
import type { ReactNode } from 'react';
import { clean, joinMeta, SR_ONLY, type MetaItem } from './meta';
import CardImg from './CardImg';
import './ds2.css';

/** dataAttrs — 항목 <div> 에 그대로 붙일 data-*(예: data-metric — 게이트 G21). */
export interface HeroStat { label: string; value: string | number; unit?: string | null; dataAttrs?: Record<`data-${string}`, string> }

export interface HeroFeature {
  title: string;
  href?: string | null;
  imageSrc?: string | null;
  imageAlt?: string | null;
  imageCaption?: string | null;
  /** 우상단 칩(「AI 상상도」). 공식 이미지면 null. */
  aiLabel?: string | null;
  /** vx_scale1 E · v2 §J — 이미지 하단 사실 배지(「3개동 · 최고 15층 · 총 N세대」 · 12px). 이미지가 있을 때만. */
  imageFacts?: string | null;
  /** post1 1b — 이미지 출처 한 줄(위성 배너 = lib/attribution/vworld 평문 · 카드 전체가 링크라 <a> 없이 12px). 이미지가 있을 때만. */
  imageCredit?: string | null;
  /** 흰 태그(「청약 D-3 · 10/19 접수」). */
  badge?: string | null;
  /** region_home1b — 흰 태그 앞 범위 칩(「전국」 — 지역 안에 현장이 없어 전국 순위를 낼 때만). 없으면 마크업 종전 그대로. */
  scopeChip?: string | null;
  meta?: MetaItem[];
}

export interface HeroProps {
  eyebrow?: ReactNode;
  title: ReactNode;
  titleAs?: 'h1' | 'h2' | 'p';
  titleClassName?: string;
  /** 주면 <H> 대신 그대로 렌더(홈 h1 처럼 태그·문자열 바이트 불변이 필요한 자리). title 은 무시. */
  titleSlot?: ReactNode;
  sub?: ReactNode;
  /** 검색 자리 — 검색창 한 벌(<KdSearchBox src="hero" size="lg"/>)을 꽂는다. */
  search?: ReactNode;
  chips?: Array<{ label: string; href: string; current?: boolean }>;
  chipsAriaLabel?: string;
  stats?: HeroStat[];
  feature?: HeroFeature | null;
  /** region_home1 — 주면 숫자 4 자리(<dl>)에 그대로 렌더(홈 지역 섬 · HeroStats 와 같은 마크업). stats 는 무시. */
  statsSlot?: ReactNode;
  /** region_home1 — 주면 대표 현장 카드 자리에 그대로 렌더(홈 지역 섬 · 캐러셀). feature 는 무시. */
  featureSlot?: ReactNode;
  /** 'hub' = 지역 허브(h1 32/25 · 모바일 숫자 양끝 정렬 20). */
  variant?: 'home' | 'hub';
  className?: string;
}

/* search_unify1 — 시안 .hbox 검색 폼(HeroSearch · .ds2-hbox) 제거: 검색창은 kd-gs 한 벌(components/search/KdSearchBox)만. */

/** 숫자 4(<dl class="ds2-stats">) — Hero 와 홈 지역 섬이 같은 마크업을 쓴다. 칸이 없으면 null. */
export function HeroStats({ stats }: { stats?: HeroStat[] | null }) {
  const st = (stats ?? []).filter((s) => clean(s.label) && clean(s.value)).slice(0, 4);
  if (!st.length) return null;
  return (
    <dl className="ds2-stats">
      {st.map((s) => (
        <div key={s.label} {...(s.dataAttrs ?? {})}>
          <dt>{s.label}</dt>
          <dd>{typeof s.value === 'number' ? s.value.toLocaleString('ko-KR') : s.value}{clean(s.unit) ? <small>{s.unit}</small> : null}</dd>
        </div>
      ))}
    </dl>
  );
}

/** 대표 현장 카드(.ds2-feat) — Hero 와 홈 지역 섬(캐러셀 슬라이드)이 같은 마크업을 쓴다. 제목이 없으면 null(빈 카드 0). */
export function HeroFeatureCard({ feature, loading = 'eager' }: { feature?: HeroFeature | null; loading?: 'eager' | 'lazy' }) {
  const f = feature && clean(feature.title) ? feature : null;
  if (!f) return null;
  const fm = joinMeta(f.meta);
  const fsrc = clean(f.imageSrc) ? String(f.imageSrc) : '';
  const fcap = clean(f.imageCaption) ? String(f.imageCaption) : '';
  const inner = (
    <>
      {fsrc ? (
        // ui1 — 깨짐 폴백(CardImg: 실패 시 카드 루트에 data-noimg → 밝은 텍스트 카드 · 남색 자리표시 금지)
        <CardImg src={fsrc} alt={clean(f.imageAlt) ? String(f.imageAlt) : ''} title={fcap || undefined} width={560} height={420} loading={loading} fetchPriority={loading === 'eager' ? 'high' : undefined} />
      ) : null}
      {fcap ? <span style={SR_ONLY}>{fcap}</span> : null}
      {clean(f.aiLabel) && fsrc ? <span className="ds2-feat__ai">{f.aiLabel}</span> : null}
      <div className="ds2-feat__ov">
        {clean(f.scopeChip) ? <span className="ds2-feat__tag" data-ds2-scope="">{f.scopeChip}</span> : null}
        {clean(f.badge) ? <span className="ds2-feat__tag">{f.badge}</span> : null}
        <p className="ds2-feat__nm">{f.title}</p>
        {fm ? <p className="ds2-feat__m">{fm}</p> : null}
        {clean(f.imageCredit) && fsrc ? <p className="ds2-feat__fx" data-sat-credit="">{String(f.imageCredit).trim()}</p> : null}
        {clean(f.imageFacts) && fsrc ? <p className="ds2-feat__fx" data-scale-badge="">{String(f.imageFacts).trim()}</p> : null}
      </div>
    </>
  );
  return f.href
    ? <a className="ds2-feat" href={f.href} data-ds2-card="feature" {...(fsrc ? {} : { 'data-noimg': '' })}>{inner}</a>
    : <div className="ds2-feat" data-ds2-card="feature" {...(fsrc ? {} : { 'data-noimg': '' })}>{inner}</div>;
}

/** region_home1b 별칭 — 캐러셀(region-b)이 쓰는 이름. HeroFeatureCard 와 같은 부품(마크업 1벌). */
export const FeatureCard = HeroFeatureCard;

export default function Hero({ eyebrow, title, titleAs = 'h1', titleClassName, titleSlot, sub, search, chips, chipsAriaLabel = '시도 바로가기', stats, feature, statsSlot, featureSlot, variant = 'home', className }: HeroProps) {
  const H = titleAs;
  const ch = (chips ?? []).filter((c) => clean(c.label) && clean(c.href)).slice(0, 6);
  return (
    <div className={className ? `ds2-hero ${className}` : 'ds2-hero'} data-ds2="hero" {...(variant === 'hub' ? { 'data-variant': 'hub' } : {})}>
      <div>
        {eyebrow ? <p className="ds2-hero__eb">{eyebrow}</p> : null}
        {titleSlot ?? <H className={titleClassName ? `ds2-hero__h1 ${titleClassName}` : 'ds2-hero__h1'}>{title}</H>}
        {sub ? <p className="ds2-hero__sub">{sub}</p> : null}
        {search ?? null}
        {ch.length ? (
          <ul className="ds2-chips" aria-label={chipsAriaLabel}>
            {ch.map((c) => (
              <li key={c.href}><a href={c.href} {...(c.current ? { 'aria-current': 'page' as const } : {})}>{c.label}</a></li>
            ))}
          </ul>
        ) : null}
        {statsSlot !== undefined ? statsSlot : <HeroStats stats={stats} />}
      </div>
      {featureSlot !== undefined ? featureSlot : <HeroFeatureCard feature={feature} />}
    </div>
  );
}
